import api from './client';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileViewer } from '@capacitor/file-viewer';

export async function descargarRelacionPdf(relacionId) {
  const res = await api.get(`/reportes/relacion/${relacionId}/pdf`, {
    responseType: 'arraybuffer',
    timeout: 60000,
  });

  const dispo = res.headers?.['content-disposition'] || '';
  const match = dispo.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = match?.[1] || `RelacionSoporte_${relacionId}.pdf`;

  const bytes = await toBytes(res.data);

  if (Capacitor.isNativePlatform()) {
    // Guardar PDF en el sistema de archivos del dispositivo
    const base64 = bytesToBase64(bytes);
    const write = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    });
    // Abrir con el visor nativo (QuickLook en iOS, Documentos en Android)
    // En lugar del iframe con data:URL que WKWebView no renderiza correctamente.
    try {
      await FileViewer.openDocumentFromLocalPath({ path: write.uri });
    } catch (e) {
      // Fallback: mostrar el iframe con file://
      showPdfOverlay(write.uri, filename, write.uri);
    }
  } else {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}

async function toBytes(data) {
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (data instanceof Uint8Array) return data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return new Uint8Array(await data.arrayBuffer());
  if (typeof data === 'string') {
    const cleaned = data.startsWith('data:') ? data.split(',')[1] : data;
    try {
      const bin = atob(cleaned);
      const out = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      return out;
    } catch (_) {
      // Fallback: tratar como Latin-1 (cada char → 1 byte) para evitar
      // "The string contains invalid characters" cuando @capacitor/http
      // nativo devuelve un string que no es base64 puro.
      const out = new Uint8Array(data.length);
      for (let i = 0; i < data.length; i++) out[i] = data.charCodeAt(i) & 0xFF;
      return out;
    }
  }
  throw new Error('Formato de respuesta no soportado: ' + typeof data);
}

function bytesToBase64(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function showPdfOverlay(dataUrl, filename, fileUri) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:#0a0e14;z-index:9999;display:flex;flex-direction:column';

  const bar = document.createElement('div');
  bar.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:8px;padding:10px 14px;background:#11151c;color:#fff;font-family:system-ui';

  const title = document.createElement('span');
  title.textContent = filename;
  title.style.cssText = 'font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1';

  let z = 1;
  const zoomOut = mkBtn('−', () => setZoom(cur => Math.max(1, +(cur - 0.25).toFixed(2))));
  const zoomReset = mkBtn('100%', () => setZoom(1));
  const zoomIn = mkBtn('+', () => setZoom(cur => Math.min(4, +(cur + 0.25).toFixed(2))));
  const save = mkBtn('⬇ Guardar', async () => {
    try {
      await Share.share({ title: filename, files: [fileUri], dialogTitle: 'Guardar PDF' });
    } catch (e) {
      alert('No se pudo abrir el menú para guardar: ' + e.message);
    }
  });
  save.style.background = '#0891b2';
  const close = mkBtn('✕', () => document.body.removeChild(overlay));
  close.style.background = '#374151';

  bar.appendChild(title);
  bar.appendChild(zoomOut);
  bar.appendChild(zoomReset);
  bar.appendChild(zoomIn);
  bar.appendChild(save);
  bar.appendChild(close);

  const scroll = document.createElement('div');
  scroll.style.cssText = 'flex:1;overflow:auto;background:#1f2937;-webkit-overflow-scrolling:touch';

  const iframe = document.createElement('iframe');
  iframe.src = dataUrl;
  iframe.style.cssText = 'width:100%;height:100%;border:0;background:#1f2937;transform-origin:top center;transition:transform .15s';
  iframe.style.transform = 'scale(1)';

  scroll.appendChild(iframe);
  overlay.appendChild(bar);
  overlay.appendChild(scroll);
  document.body.appendChild(overlay);

  function setZoom(next) {
    z = typeof next === 'function' ? next(z) : next;
    iframe.style.transform = `scale(${z})`;
    iframe.style.width = `${100 * z}%`;
    iframe.style.height = `${100 * z}%`;
    zoomReset.textContent = `${Math.round(z * 100)}%`;
  }
}

function mkBtn(label, onClick) {
  const b = document.createElement('button');
  b.textContent = label;
  b.style.cssText = 'padding:6px 10px;background:#1f2937;color:#fff;border:0;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;flex-shrink:0;min-width:36px';
  b.onclick = onClick;
  return b;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}